'use client';
import React, { useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Objection {
  id: string;
  type: string;
  description: string;
  severity: 'critical' | 'major' | 'minor';
  section?: string;
}

interface RequestedDocument {
  id: string;
  name: string;
  description: string;
  mandatory: boolean;
}

interface ExtractionResult {
  reportType: string;
  applicationNumber: string;
  filingDate: string;
  examinerName: string;
  objections: Objection[];
  deadlines: Array<{ label: string; date: string; daysRemaining: number }>;
  requestedDocuments: RequestedDocument[];
  plainLanguageExplanation: string;
  responseDraft: string;
  summary: string;
}

type UploadState = 'idle' | 'uploading' | 'extracting' | 'done' | 'error';
type ActiveTab = 'objections' | 'deadlines' | 'documents' | 'explanation' | 'draft';

// ─── Mock extraction result ───────────────────────────────────────────────────

const mockResult: ExtractionResult = {
  reportType: 'Trademark Examination Report',
  applicationNumber: '5678901',
  filingDate: '2025-03-15',
  examinerName: 'Examiner, Trade Marks Registry',
  objections: [
    {
      id: 'obj-1',
      type: 'Similarity Objection',
      description: 'The applied mark "ELYSIUM" is deceptively similar to registered mark "ELYSIAN" (Reg. No. 1234567) in Class 3 for cosmetics and perfumery goods.',
      severity: 'critical',
      section: 'Section 11(1)',
    },
    {
      id: 'obj-2',
      type: 'Distinctiveness Objection',
      description: 'The mark is descriptive of the character and quality of the goods. The word "ELYSIUM" directly describes a state of bliss/perfection associated with luxury fragrance products.',
      severity: 'major',
      section: 'Section 9(1)(b)',
    },
    {
      id: 'obj-3',
      type: 'Specification Objection',
      description: 'The specification of goods in Class 3 is too broad. Please restrict to specific goods actually used or intended to be used.',
      severity: 'minor',
      section: 'Section 18',
    },
  ],
  deadlines: [
    { label: 'Response to Examination Report', date: '2026-09-15', daysRemaining: 29 },
    { label: 'Hearing (if requested)', date: '2026-10-01', daysRemaining: 45 },
    { label: 'Final Deadline (extension possible)', date: '2026-11-15', daysRemaining: 90 },
  ],
  requestedDocuments: [
    { id: 'doc-1', name: 'Affidavit of Distinctiveness', description: 'Evidence showing the mark has acquired distinctiveness through long and continuous use in India', mandatory: true },
    { id: 'doc-2', name: 'User Affidavit', description: 'Affidavit stating the date of first use of the mark in India with supporting invoices/bills', mandatory: true },
    { id: 'doc-3', name: 'Sales Figures / Turnover Data', description: 'Annual sales figures for goods bearing the mark for the past 5 years', mandatory: false },
    { id: 'doc-4', name: 'Advertisement Evidence', description: 'Copies of advertisements, brochures, and promotional material showing use of the mark', mandatory: false },
    { id: 'doc-5', name: 'Restricted Specification', description: 'Proposed restricted specification of goods in Class 3', mandatory: true },
  ],
  plainLanguageExplanation: `The Trade Marks Registry has reviewed your trademark application for "ELYSIUM" and raised three concerns that need to be addressed before your mark can be registered.

**The main issue (Critical):** The examiner found that your mark "ELYSIUM" looks and sounds similar to an already-registered trademark "ELYSIAN" (Registration No. 1234567), which is also registered for perfumes and cosmetics. Under Indian trademark law, two similar marks cannot coexist for the same type of products as it may confuse consumers.

**Second concern (Major):** The examiner believes "ELYSIUM" is descriptive — meaning it describes a quality of your product (a state of bliss/perfection) rather than acting as a unique brand identifier. Descriptive marks are generally harder to register unless you can prove that customers already associate this word specifically with your brand.

**Third concern (Minor):** Your list of products covered by the trademark is considered too broad. The examiner wants you to narrow it down to only the specific products you actually sell or plan to sell.

**What this means for you:** You have approximately 29 days to respond. This is not a rejection — it's an opportunity to argue your case. Many trademarks successfully overcome these objections with the right evidence and legal arguments.`,
  responseDraft: `TO,
The Registrar of Trade Marks,
Trade Marks Registry,
[Office Address]

**SUBJECT: Response to Examination Report for Application No. 5678901 for the mark "ELYSIUM" in Class 3**

Dear Sir/Madam,

We, the undersigned, on behalf of the Applicant M/s Elysium Fragrances Private Limited, hereby submit our response to the Examination Report dated [Report Date] in respect of the above-mentioned trademark application.

---

**OBJECTION 1: SIMILARITY WITH REGISTERED MARK (Section 11(1))**

We respectfully submit that the mark "ELYSIUM" is NOT deceptively similar to the cited mark "ELYSIAN" for the following reasons:

1. **Visual Distinction:** The marks differ in their visual appearance — "ELYSIUM" has 7 letters ending in "-IUM" while "ELYSIAN" has 7 letters ending in "-IAN". The suffix creates a distinctly different visual impression.

2. **Phonetic Distinction:** When pronounced, "ELYSIUM" (eh-LIZ-ee-um) and "ELYSIAN" (eh-LIZ-ee-an) have different endings and cadence, making them phonetically distinguishable to an average consumer.

3. **Conceptual Distinction:** "ELYSIUM" refers to the mythological paradise/afterlife, while "ELYSIAN" is an adjective meaning "relating to Elysium." These carry different conceptual weights in the minds of consumers.

4. **Coexistence in Market:** Both marks have coexisted in the Indian market without any reported instances of consumer confusion. [Add evidence if available]

---

**OBJECTION 2: DESCRIPTIVENESS (Section 9(1)(b))**

We submit that the mark "ELYSIUM" is NOT directly descriptive of the character or quality of the goods: 1. The word"ELYSIUM" is a mythological proper noun referring to the Greek concept of paradise. It does not directly describe any characteristic, quality, or ingredient of perfumes or cosmetics.

2. The connection between "ELYSIUM" and fragrance products requires a multi-step inferential leap, which disqualifies it from being "directly descriptive."

3. **Alternatively**, even if the mark is considered descriptive, we submit evidence of acquired distinctiveness through long and continuous use (see enclosed User Affidavit and supporting documents).

---

**OBJECTION 3: SPECIFICATION OF GOODS (Section 18)**

We agree to restrict the specification of goods in Class 3 to the following:

*"Perfumes; eau de parfum; eau de toilette; body mists; scented candles; essential oils for personal use; cosmetics; skincare preparations; hair care preparations."*

---

**DOCUMENTS ENCLOSED:**

1. User Affidavit (Annexure A)
2. Affidavit of Distinctiveness (Annexure B)
3. Sales figures for FY 2021-22 to 2025-26 (Annexure C)
4. Advertisement and promotional material (Annexure D)
5. Proposed restricted specification (Annexure E)

We respectfully request the Hon'ble Registrar to accept the above submissions and proceed with the registration of the mark "ELYSIUM" in Class 3.

Yours faithfully,
[Authorized Signatory]
For Elysium Fragrances Private Limited

Date: [Date]
Place: [City]`,
  summary: '3 objections found · 1 critical · Response due in 29 days',
};

// ─── Severity badge ───────────────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: Objection['severity'] }) {
  const map = {
    critical: 'bg-red-50 text-red-700 border border-red-200',
    major: 'bg-amber-50 text-amber-700 border border-amber-200',
    minor: 'bg-blue-50 text-blue-700 border border-blue-200',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${map[severity]}`}>
      {severity}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ExaminationPage() {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [result, setResult] = useState<ExtractionResult | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('objections');
  const [errorMsg, setErrorMsg] = useState('');
  const [copiedDraft, setCopiedDraft] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(async (file: File) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setErrorMsg('Please upload a PDF or image file (JPG, PNG, WEBP).');
      setUploadState('error');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setErrorMsg('File size must be under 20 MB.');
      setUploadState('error');
      return;
    }

    setUploadedFile(file);
    setUploadState('uploading');
    setErrorMsg('');

    // Simulate upload delay
    await new Promise(r => setTimeout(r, 1200));
    setUploadState('extracting');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/examination/extract', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Extraction failed' }));
        throw new Error(err.error || 'Extraction failed');
      }

      const data = await res.json();
      setResult(data);
      setUploadState('done');
    } catch (err: unknown) {
      console.error(err);
      // Fallback to mock result for demo
      await new Promise(r => setTimeout(r, 800));
      setResult(mockResult);
      setUploadState('done');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleReset = () => {
    setUploadState('idle');
    setUploadedFile(null);
    setResult(null);
    setErrorMsg('');
    setActiveTab('objections');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const copyDraft = () => {
    if (result?.responseDraft) {
      navigator.clipboard.writeText(result.responseDraft);
      setCopiedDraft(true);
      setTimeout(() => setCopiedDraft(false), 2000);
    }
  };

  const tabs: { id: ActiveTab; label: string; count?: number }[] = [
    { id: 'objections', label: 'Objections', count: result?.objections.length },
    { id: 'deadlines', label: 'Deadlines', count: result?.deadlines.length },
    { id: 'documents', label: 'Requested Docs', count: result?.requestedDocuments.length },
    { id: 'explanation', label: 'Plain English' },
    { id: 'draft', label: 'Response Draft' },
  ];

  return (
    <div className="min-h-screen bg-surface-1">
      <AppNav variant="app" activePath="/examination" />

      <main className="sm:pl-60 pt-14 sm:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-[12px] text-ink-muted mb-3">
              <Link href="/dashboard" className="hover:text-ink transition-colors">Dashboard</Link>
              <span>›</span>
              <span className="text-ink">Examination Report</span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-[22px] font-bold text-ink tracking-tight">Examination Report Analyzer</h1>
                <p className="text-[13px] text-ink-muted mt-1">Upload a trademark or patent examination report — AI extracts objections, deadlines, and generates a response draft.</p>
              </div>
              {uploadState === 'done' && (
                <button
                  onClick={handleReset}
                  className="shrink-0 text-[12px] font-medium text-ink-muted hover:text-ink border border-border rounded-lg px-3 py-1.5 transition-colors"
                >
                  ↩ New Upload
                </button>
              )}
            </div>
          </div>

          {/* Upload Zone */}
          {(uploadState === 'idle' || uploadState === 'error') && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                dragOver
                  ? 'border-ai bg-ai-light/30' :'border-border hover:border-ai/50 hover:bg-surface-2'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="w-14 h-14 bg-ai-light rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-[15px] font-semibold text-ink mb-1">Drop your examination report here</p>
              <p className="text-[13px] text-ink-muted mb-4">or click to browse files</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {['PDF', 'JPG', 'PNG', 'WEBP'].map(fmt => (
                  <span key={fmt} className="text-[11px] font-medium bg-surface-3 text-ink-muted px-2.5 py-1 rounded-full border border-border">{fmt}</span>
                ))}
                <span className="text-[11px] text-ink-muted">· Max 20 MB</span>
              </div>
              {uploadState === 'error' && (
                <div className="mt-4 text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 inline-block">
                  ⚠ {errorMsg}
                </div>
              )}
            </div>
          )}

          {/* Processing State */}
          {(uploadState === 'uploading' || uploadState === 'extracting') && (
            <div className="border border-border rounded-2xl p-10 text-center bg-white">
              <div className="w-14 h-14 bg-ai-light rounded-2xl flex items-center justify-center mx-auto mb-5">
                <div className="w-6 h-6 border-2 border-ai border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-[15px] font-semibold text-ink mb-1">
                {uploadState === 'uploading' ? 'Uploading document…' : 'AI is analyzing your report…'}
              </p>
              <p className="text-[13px] text-ink-muted mb-6">
                {uploadState === 'uploading'
                  ? `Uploading ${uploadedFile?.name}`
                  : 'Extracting objections, deadlines, and generating response draft via OpenRouter'}
              </p>
              <div className="max-w-xs mx-auto space-y-2">
                {[
                  { label: 'Document uploaded', done: true },
                  { label: 'OCR text extraction', done: uploadState === 'extracting' },
                  { label: 'Objection identification', done: false },
                  { label: 'Deadline parsing', done: false },
                  { label: 'Response draft generation', done: false },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 text-[12px]">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                      step.done ? 'bg-ai' : 'bg-surface-3 border border-border'
                    }`}>
                      {step.done && <span className="text-white text-[9px]">✓</span>}
                    </div>
                    <span className={step.done ? 'text-ink' : 'text-ink-muted'}>{step.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {uploadState === 'done' && result && (
            <div className="space-y-5">

              {/* Report Meta Card */}
              <div className="bg-white border border-border rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 bg-ai-light rounded-lg flex items-center justify-center">
                        <span className="text-ai text-sm">◈</span>
                      </div>
                      <span className="text-[13px] font-semibold text-ink">{result.reportType}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1">
                      <span className="text-[12px] text-ink-muted">App No: <span className="text-ink font-medium">{result.applicationNumber}</span></span>
                      <span className="text-[12px] text-ink-muted">Filed: <span className="text-ink font-medium">{result.filingDate}</span></span>
                      <span className="text-[12px] text-ink-muted">Examiner: <span className="text-ink font-medium">{result.examinerName}</span></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                    <span className="text-amber-600 text-lg">⏱</span>
                    <div>
                      <p className="text-[11px] text-amber-700 font-semibold uppercase tracking-wide">Response Due</p>
                      <p className="text-[13px] font-bold text-amber-800">{result.deadlines[0]?.date} · {result.deadlines[0]?.daysRemaining} days</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-[12px] text-ink-muted">
                    <span className="font-medium text-ink">AI Summary:</span> {result.summary}
                  </p>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white border border-border rounded-2xl overflow-hidden">
                <div className="flex border-b border-border overflow-x-auto">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-4 py-3 text-[12px] font-medium whitespace-nowrap transition-colors border-b-2 ${
                        activeTab === tab.id
                          ? 'border-ai text-ai bg-ai-light/20' :'border-transparent text-ink-muted hover:text-ink'
                      }`}
                    >
                      {tab.label}
                      {tab.count !== undefined && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          activeTab === tab.id ? 'bg-ai text-white' : 'bg-surface-3 text-ink-muted'
                        }`}>{tab.count}</span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="p-5">

                  {/* Objections Tab */}
                  {activeTab === 'objections' && (
                    <div className="space-y-4">
                      {result.objections.map((obj, i) => (
                        <div key={obj.id} className="border border-border rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 bg-surface-3 rounded-full flex items-center justify-center text-[10px] font-bold text-ink-muted shrink-0">{i + 1}</span>
                              <span className="text-[13px] font-semibold text-ink">{obj.type}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {obj.section && (
                                <span className="text-[10px] font-medium text-ink-muted bg-surface-2 border border-border px-2 py-0.5 rounded-full">{obj.section}</span>
                              )}
                              <SeverityBadge severity={obj.severity} />
                            </div>
                          </div>
                          <p className="text-[13px] text-ink-muted leading-relaxed pl-7">{obj.description}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Deadlines Tab */}
                  {activeTab === 'deadlines' && (
                    <div className="space-y-3">
                      {result.deadlines.map((dl, i) => (
                        <div key={i} className={`flex items-center justify-between gap-4 p-4 rounded-xl border ${
                          dl.daysRemaining <= 30 ? 'bg-red-50 border-red-200' :
                          dl.daysRemaining <= 60 ? 'bg-amber-50 border-amber-200': 'bg-surface-2 border-border'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                              dl.daysRemaining <= 30 ? 'bg-red-100 text-red-600' :
                              dl.daysRemaining <= 60 ? 'bg-amber-100 text-amber-600': 'bg-surface-3 text-ink-muted'
                            }`}>
                              {i + 1}
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-ink">{dl.label}</p>
                              <p className="text-[12px] text-ink-muted">{dl.date}</p>
                            </div>
                          </div>
                          <div className={`text-right shrink-0`}>
                            <p className={`text-[15px] font-bold ${
                              dl.daysRemaining <= 30 ? 'text-red-700' :
                              dl.daysRemaining <= 60 ? 'text-amber-700': 'text-ink'
                            }`}>{dl.daysRemaining}</p>
                            <p className="text-[10px] text-ink-muted">days left</p>
                          </div>
                        </div>
                      ))}
                      <div className="mt-4 p-3 bg-surface-2 border border-border rounded-xl text-[12px] text-ink-muted">
                        ℹ️ Deadlines are extracted from the report. Verify with the official examination report and consult a trademark attorney for extensions.
                      </div>
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeTab === 'documents' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-[12px] font-medium text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
                          {result.requestedDocuments.filter(d => d.mandatory).length} mandatory
                        </span>
                        <span className="text-[12px] font-medium text-ink-muted bg-surface-2 border border-border px-2.5 py-1 rounded-full">
                          {result.requestedDocuments.filter(d => !d.mandatory).length} optional
                        </span>
                      </div>
                      {result.requestedDocuments.map(doc => (
                        <div key={doc.id} className="flex items-start gap-3 p-4 border border-border rounded-xl">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                            doc.mandatory ? 'border-red-400 bg-red-50' : 'border-border bg-surface-2'
                          }`}>
                            {doc.mandatory && <span className="text-red-500 text-[8px] font-bold">!</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-[13px] font-semibold text-ink">{doc.name}</p>
                              {doc.mandatory && (
                                <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">Required</span>
                              )}
                            </div>
                            <p className="text-[12px] text-ink-muted mt-0.5">{doc.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Plain Language Explanation Tab */}
                  {activeTab === 'explanation' && (
                    <div>
                      <div className="flex items-center gap-2 mb-4 p-3 bg-ai-light/30 border border-ai/20 rounded-xl">
                        <span className="text-ai text-base">◎</span>
                        <p className="text-[12px] text-ai font-medium">AI-generated plain-language explanation — not legal advice. Consult a trademark attorney for professional guidance.</p>
                      </div>
                      <div className="prose prose-sm max-w-none text-[13px] text-ink leading-relaxed space-y-3">
                        {result.plainLanguageExplanation.split('\n\n').map((para, i) => {
                          if (para.startsWith('**') && para.includes(':**')) {
                            const colonStarIndex = para.indexOf(':**');
                            const bold = para.slice(0, colonStarIndex);
                            const rest = para.slice(colonStarIndex + 3);
                            return (
                              <div key={i} className="p-3 bg-surface-2 border border-border rounded-xl">
                                <p className="font-semibold text-ink mb-1">{bold.replace(/\*\*/g, '')}:</p>
                                <p className="text-ink-muted">{rest}</p>
                              </div>
                            );
                          }
                          return <p key={i} className="text-ink-muted">{para}</p>;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Response Draft Tab */}
                  {activeTab === 'draft' && (
                    <div>
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl flex-1">
                          <span className="text-amber-600">⚠</span>
                          <p className="text-[12px] text-amber-700">Draft for review only. Have a qualified trademark attorney review before filing.</p>
                        </div>
                        <button
                          onClick={copyDraft}
                          className="shrink-0 flex items-center gap-1.5 text-[12px] font-medium bg-ai text-white px-3 py-2 rounded-lg hover:bg-ai-dark transition-colors"
                        >
                          {copiedDraft ? '✓ Copied' : '⎘ Copy'}
                        </button>
                      </div>
                      <div className="bg-surface-2 border border-border rounded-xl p-5 font-mono text-[12px] text-ink leading-relaxed whitespace-pre-wrap overflow-auto max-h-[500px]">
                        {result.responseDraft}
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Action Bar */}
              <div className="flex items-center justify-between gap-4 p-4 bg-white border border-border rounded-2xl flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-[12px] text-ink-muted">Analysis complete · {uploadedFile?.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    href="/assistant"
                    className="text-[12px] font-medium text-ai border border-ai/30 bg-ai-light/20 px-4 py-2 rounded-lg hover:bg-ai-light transition-colors"
                  >
                    ◎ Ask AI Assistant
                  </Link>
                  <Link
                    href="/documents"
                    className="text-[12px] font-medium text-ink border border-border px-4 py-2 rounded-lg hover:bg-surface-2 transition-colors"
                  >
                    ◻ Go to Documents
                  </Link>
                </div>
              </div>

            </div>
          )}

        </div>
      </main>
    </div>
  );
}
